/* eslint-disable consistent-return */
/* eslint-disable no-unused-expressions */
/* eslint-disable no-param-reassign */
/* eslint-disable no-use-before-define */
import { RxSocketUrls } from '@Constants/CommonConstants';
import { resultsConfig } from '@Constants/Results';
import ResultsInterfaceBuffer from '@Services/ResultsInterface/ResultsInterfaceBuffer';
import ResultsCache from '@Services/ResultsCache';
import EmptyResultsException from '@Services/EmptyResultsException';
import uuid from 'uuid/v4';
import { getValueByNs, deepClone } from '@Hooks/LibraryInitialization/MoreUnderscore';
import * as ResultsDataManage from '@Services/ResultsDataManage';
import * as Results from '@Services/Results';
import * as FormatUtils from '@Services/FormatUtils';
import RxSocket from '@Services/RxSocket';
import SocketBroker from '@Services/SocketBroker';
import each from 'lodash/each';
import isEmpty from 'lodash/isEmpty';
import keys from 'lodash/keys';
import range from 'lodash/range';
import last from 'lodash/last';
import map from 'lodash/map';
import defaults from 'lodash/defaults';
import { Subject } from 'rxjs';

const { chunkSizePerContext } = resultsConfig;
const COMBINED_RESPONSE_SUPPORTED_CONTEXTS = ['reactions'];

const unsubs = {
  info: null,
  custom: null,
  connect: null,
  close: null,
};

const buffer = new ResultsInterfaceBuffer(); // storing info and custom messages for active request in one place
let resultsSubject = null;
let queue = []; // queue contains only chunk requests for the same results page
let activeRequest; // interface performing one request at time and waits til it satisfied

window.ResultsInterfaceBuffer = buffer;

export const init = () => {
  subscribe();

  if (!unsubs.close) {
    unsubs.close = RxSocket.onClose(() => {
      destroy(['info', 'custom']);
    });
  }
  if (!unsubs.connect) {
    unsubs.connect = RxSocket.onConnect(subscribe);
  }

  function subscribe() {
    const { ResultsInfo, ResultsCustom } = RxSocketUrls.ChannelsUrls;

    if (!unsubs.info) {
      unsubs.info = SocketBroker.observe(ResultsInfo, subsCallback.bind(null, 'info'));
    }
    if (!unsubs.custom) {
      unsubs.custom = SocketBroker.observe(ResultsCustom, subsCallback.bind(null, 'custom'));
    }

    activeRequest && next(activeRequest);
  }
};

export const destroy = (events) => {
  // either specific events (for ws reconnecting), or all of them (when main controller destroyed)
  events = events || keys(unsubs);

  each(events, (name) => {
    unsubs[name] && unsubs[name]();
    unsubs[name] = null;
  });
};

export const getResults = (request, isPiggyback) => {
  Logger.info('ResultsInterface.getResults', request);
  if (resultsSubject && !isPiggyback) {
    queue = [];
    activeRequest.canceled = true;
    resultsSubject.complete();
    resultsSubject = null;
  }

  createSequence(request, isPiggyback);
  if (!activeRequest) {
    next();
  }
  if (!resultsSubject || !isPiggyback) {
    resultsSubject = new Subject();
  }
  resultsSubject.cancel = () => cleanUp(true);

  return resultsSubject;
};

const subsCallback = (type, response) => {
  const requestId = getValueByNs(response, 'info.query.requestId', null);
  if (!requestId) {
    throw new Error('requestId is not provided in the back-end response');
  }
  if (!activeRequest || activeRequest.requestId !== requestId) {
    return; // skip if nothing is requested or response is not caused by active request
  }

  _logResponse(type, response);

  if (type === 'info' && response.payload && isEmpty(response.payload.entities)) return handleUnexpectedEmptyResults();

  const { context, resultName } = activeRequest;
  if (context === 'citations' || isCombinedResponse(response)) {
    // citations do not expect messages from custom channel
    return respond(response);
  }

  buffer.put(type, response);
  if (buffer.isFull()) {
    ResultsDataManage.updateWithExtraInfo(context, buffer.info.payload.entities, buffer.custom, resultName);
    respond(buffer.info);
    buffer.clear();
  }

  function isCombinedResponse(responseVal) {
    return (
      getValueByNs(responseVal, 'info.query.combinedResponse', false) &&
      isCombinedResponseSupported(getValueByNs(responseVal, 'info.query.context', false))
    );
  }
};

const createSequence = (params, isPiggyBack) => {
  const { firstItem, lastItem } = params;
  const step = chunkSizePerContext[params.context];
  let sequenceRange = range(firstItem, lastItem, step);
  if (!sequenceRange.length) {
    sequenceRange = [firstItem];
  }

  if (lastItem - last(sequenceRange) === step) {
    // range makes the same array for (1, 5, 5) and (1, 6, 5)
    sequenceRange.push(lastItem);
  }

  const chunks = map(sequenceRange, (item) => {
    let lastVal = item + step - 1;
    if (lastVal > lastItem) {
      lastVal = lastItem;
    }
    return defaults({ firstItem: item, lastItem: lastVal }, params, { requestId: uuid() });
  });

  if (isPiggyBack) {
    queue.push(...chunks);
  } else {
    queue.splice(0, 0, ...chunks);
  }
};

const next = (request) => {
  if (request || queue.length) {
    activeRequest = request || queue.shift(); // only one active request at time, preventing request loop
    activeRequest.combinedResponse = isCombinedResponseSupported(activeRequest.context) ? activeRequest.combinedResponse : false;

    const cache = ResultsCache.get(activeRequest);
    if (cache) {
      return setTimeout(() => respond(cache, true)); // timeout is used to ensure that promise is created and returned before respond
    }

    return setTimeout(() => {
      const {
        resultName,
        databaseId,
        context,
        firstItem,
        lastItem,
        sort,
        order,
        hitSetKey,
        requestId,
        similarityMol,
        isHeatmapCellDetails,
        coResults,
        forPlanner,
        combinedResponse,
        filterApplied,
        semanticSearchIncluded,
        semanticResultName,
        textQuery,
      } = activeRequest;

      activeRequest.requestId = Results.getResultsById(
        resultName,
        databaseId,
        context,
        firstItem,
        lastItem,
        sort,
        order,
        hitSetKey,
        requestId,
        similarityMol,
        isHeatmapCellDetails,
        coResults,
        forPlanner,
        combinedResponse,
        filterApplied,
        semanticSearchIncluded,
        semanticResultName,
        textQuery
      );
    }, 0);
  }
};

const isCombinedResponseSupported = (context) => COMBINED_RESPONSE_SUPPORTED_CONTEXTS.indexOf(context) >= 0;

const respond = (response, fromCache = false) => {
  if (!activeRequest) return;

  let entities = response;
  if (!fromCache) {
    entities = getFormattedEntities(entities, activeRequest.databaseId);
    ResultsCache.put(activeRequest, entities);
  }

  if (!activeRequest.canceled) {
    // do not send response for previous request
    if (queue.length) {
      // if we have pending requests in a queue then process is not over
      resultsSubject.next({ type: 'progress', data: entities });
    } else {
      resultsSubject.next({ type: 'success', data: entities });
      resultsSubject.complete();
      resultsSubject = null;
    }
  }

  activeRequest = null; // reset since request is processed
  next();
};

const getFormattedEntities = (data, databaseId) => {
  const { context } = data.info.query;
  const entities = FormatUtils.formatEntitiesByContext(context, data.payload.entities);

  return map(entities, (entity, index) => {
    entity.index = index;
    entity.databaseId = entity.databaseId || databaseId;
    return entity;
  });
};

const cleanUp = (reject = true) => {
  buffer.clear();
  if (reject && resultsSubject) {
    resultsSubject.error('canceled');
    resultsSubject.complete();
  }
  resultsSubject = null;
  activeRequest = null;
  queue = [];
};

const handleUnexpectedEmptyResults = () => {
  resultsSubject.error(new EmptyResultsException(`Unexpected error of no results for result set #${activeRequest.resultName}`));
  resultsSubject.complete();
  cleanUp(false);
};

const _logResponse = (type, response) => {
  const { firstItem, lastItem, requestId } = response.info.query;
  Logger.info(`WS results ${type} ${firstItem}-${lastItem} response ${requestId}:`, () => deepClone(response));
};
